import { useQueryClient } from '@tanstack/react-query'
import React, { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link, useHistory, useLocation } from 'react-router-dom'

import { useCurrentUser } from '@/api/auth/queries/useCurrentUser'
import { useIsMobile } from '@/hooks/useIsMobile'
import { QUERY_KEYS } from '@/lib/queryKeys'
import { cn } from '@/lib/utils'

import { usePackageReqs } from '../../api/package-req/queries/usePackageReqs'
import grFlagCircle from '../../imgs/flags/greece-circle.png'
import uaeFlagCircle from '../../imgs/flags/uae-circle.png'
import ukFlagCircle from '../../imgs/flags/uk-circle.png'
import usaFlagCircle from '../../imgs/flags/usa-circle.png'
import logo from '../../imgs/logo.png'
import logoutIcon from '../../imgs/logout_icon.svg'
import { setIsAuthenticated } from '../../store/appSlice'

export default function OfficeNavbar() {
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()
  const isMobile = useIsMobile()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { data: loggedUser } = useCurrentUser()
  const queryClient = useQueryClient()
  const { data: packageRequestsData } = usePackageReqs()
  const isUserRoutes = !location.pathname.includes('/office-')
  // Calculate total sum
  const shrinkReqs = packageRequestsData?.shrinkRequest ?? []
  const photoReqs = packageRequestsData?.photoRequest ?? []
  const returnReqs = packageRequestsData?.returnRequest?.filter(returnReq => returnReq.returnLabel) ?? []
  const uniteReqs = packageRequestsData?.uniteRequest ?? []
  const allReqsSum = shrinkReqs.length + photoReqs.length + returnReqs.length + uniteReqs.length

  const logOutHandler = async () => {
    localStorage.removeItem('cognito-tokens')
    dispatch(setIsAuthenticated(false))
    queryClient.setQueryData([QUERY_KEYS.LOGGED_USER], null)
    queryClient.clear()
    history.push('/')
  }

  function toggleMenu() {
    if (!isMobile) return
    setIsMenuOpen(prev => !prev)
  }
  const isWarehouseWorker =
    (loggedUser?.permissions?.Worker || loggedUser?.permissions?.OfficeManager) &&
    !(loggedUser?.permissions?.Admin || loggedUser?.permissions?.SeniorAgent || loggedUser?.permissions?.Agent)
  const isRouteActive = (route: string) => {
    const parts = location.pathname.split('/').filter(part => part !== '')
    return parts[0] === route.slice(1)
  }
  const items = useMemo(
    () => [
      {
        label: 'Add New Package',
        to: '/office',
      },
      {
        label: 'Location',
        to: '/office-location',
      },
      {
        label: (
          <span className='flex items-center gap-1.5'>
            Package Requests
            {allReqsSum > 0 && (
              <span className='flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-semibold text-white'>
                {allReqsSum}
              </span>
            )}
          </span>
        ),
        to: '/office-packageRequests',
      },
      {
        label: 'Barcode Gun Ship',
        to: '/office-ship-barcode-gun',
      },
      {
        label: 'Ship Packages',
        to: '/office-ship',
      },
      {
        label: 'Packages Check List',
        to: '/office-checkForm',
      },
      {
        label: 'Edit & Update',
        to: '/office-e&u',
      },
      {
        label: 'Warehouse Arrivals',
        to: '/office-warehouse-arrivals',
      },
      {
        label: 'Stats',
        to: '/office-stats',
      },
      ...(!isWarehouseWorker
        ? [
            {
              label: 'Affiliate',
              to: '/office-affiliate',
            },
            {
              label: 'Coupons',
              to: '/office-coupon',
            },
            {
              label: 'Delayed Payments',
              to: '/office-delayed-payments',
            },
          ]
        : []),
    ],
    [allReqsSum, isWarehouseWorker]
  )

  const getFlag = () => {
    const warehouse = loggedUser?.UserAttributes?.['custom:warehouse']
    if (warehouse === 'USA') return usaFlagCircle
    if (warehouse === 'GR') return grFlagCircle
    if (warehouse === 'UK') return ukFlagCircle
    if (warehouse === 'UAE') return uaeFlagCircle
    return null
  }

  const flagSrc = getFlag()

  return (
    <header
      className={cn('no-print fixed start-0 top-0 z-50 w-full border-b border-gray-200 bg-white shadow-sm', isUserRoutes && 'static')}
    >
      <nav className='relative !flex h-[var(--navbar-height)] items-center justify-between px-4 md:px-6'>
        {/* Logo and Flag Section */}
        <div className='relative flex w-fit items-center gap-3 pe-8'>
          <Link to='/' className='flex items-center justify-center transition-opacity hover:opacity-80'>
            <img alt='logo' className='h-12 w-12 min-w-12' src={logo} />
          </Link>
          {flagSrc && (
            <div className='absolute end-4 bottom-0 flex items-center'>
              <img alt='flag' className='h-5 w-5' src={flagSrc} />
            </div>
          )}
        </div>

        {/* Desktop Navigation */}
        <ul className='m-0 hidden h-full list-none items-stretch justify-between overflow-x-auto p-0 md:flex'>
          {items.map(item => (
            <li key={item.to} className='flex h-full items-center border-r border-gray-200 last:border-r-0'>
              <Link
                to={item.to}
                className={cn(
                  'flex h-full items-center rounded-md px-3 text-center text-sm font-medium !text-gray-700 transition-all duration-200',
                  'hover:bg-yellow-50 hover:text-yellow-500',
                  isRouteActive(item.to) && '!bg-yellow-50 font-semibold !text-yellow-600'
                )}
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>

        {/* Logout Button */}
        <div className='flex min-w-[50px] items-center justify-end gap-4'>
          <img alt='logout' className='size-6 cursor-pointer' onClick={logOutHandler} src={logoutIcon} />

          {/* Mobile Hamburger Menu */}
          <button
            onClick={toggleMenu}
            className={cn(
              'flex flex-col gap-1.5 rounded-sm border-0 !bg-gray-100 p-2 transition-all duration-300 md:hidden',
              isMenuOpen && 'rotate-90'
            )}
            aria-label='Toggle menu'
            aria-expanded={isMenuOpen}
          >
            <span className={cn('block h-0.5 w-6 bg-gray-800 transition-all duration-300', isMenuOpen && 'translate-y-2 rotate-45')} />
            <span className={cn('block h-0.5 w-6 bg-gray-800 transition-all duration-300', isMenuOpen && 'opacity-0')} />
            <span className={cn('block h-0.5 w-6 bg-gray-800 transition-all duration-300', isMenuOpen && '-translate-y-2 -rotate-45')} />
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={cn(
            'absolute start-0 top-14 z-[100] max-h-[calc(100vh-56px)] w-full overflow-y-auto border-b border-gray-200 bg-white shadow-lg transition-all duration-300 ease-in-out md:hidden',
            isMenuOpen ? 'visible opacity-100' : 'pointer-events-none invisible opacity-0'
          )}
        >
          <ul className='flex list-none flex-col p-0 py-2'>
            {items.map(item => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  onClick={() => setIsMenuOpen(false)}
                  className={cn(
                    'block px-6 py-3 text-base font-medium !text-gray-700 transition-colors duration-200',
                    'hover:bg-yellow-50 hover:text-yellow-600',
                    isRouteActive(item.to) && 'border-s-4 border-yellow-500 bg-yellow-50 font-semibold text-yellow-600'
                  )}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </nav>
    </header>
  )
}
